import React, { Component, createRef } from "react";
import Hls from "hls.js";
import "./VideoPlayer.css"; // We'll include the CSS below

class VideoPlayerHLS extends Component {
  videoRef = createRef();
  progressRef = createRef();
  volumeRef = createRef();
  hls = null;

  state = {
    playing: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    muted: false,
    fullscreen: false,
  };

  convertToCloudFront = (url) => {
    try {
      const parsed = new URL(url);
      return "https://d35pld11nuhae4.cloudfront.net" + parsed.pathname;
    } catch (e) {
      console.error("Invalid URL:", url);
      return url;
    }
  };

  componentDidMount() {
    const video = this.videoRef.current;
    const src = this.convertToCloudFront(this.props.url);

    // Set up event listeners for custom controls
    video.addEventListener("timeupdate", this.handleTimeUpdate);
    video.addEventListener("loadedmetadata", this.handleLoadedMetadata);
    video.addEventListener("volumechange", this.handleVolumeChange);
    video.addEventListener("play", this.handlePlay);
    video.addEventListener("pause", this.handlePause);
    video.addEventListener("ended", this.handleEnded);

    if (Hls.isSupported()) {
      this.hls = new Hls({
        xhrSetup: (xhr) => {
          xhr.setRequestHeader("Accept-Encoding", "identity");
        },
      });

      this.hls.loadSource(src);
      this.hls.attachMedia(video);

      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play();
      });

      this.hls.on(Hls.Events.ERROR, (e, data) => {
        console.log("HLS error:", data);
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.play();
    }
  }

  componentWillUnmount() {
    const video = this.videoRef.current;
    video.removeEventListener("timeupdate", this.handleTimeUpdate);
    video.removeEventListener("loadedmetadata", this.handleLoadedMetadata);
    video.removeEventListener("volumechange", this.handleVolumeChange);
    video.removeEventListener("play", this.handlePlay);
    video.removeEventListener("pause", this.handlePause);
    video.removeEventListener("ended", this.handleEnded);

    if (this.hls) this.hls.destroy();
  }

  // Event handlers
  handleTimeUpdate = () => {
    const video = this.videoRef.current;
    this.setState({
      currentTime: video.currentTime,
      duration: video.duration || 0,
    });
  };

  handleLoadedMetadata = () => {
    this.setState({ duration: this.videoRef.current.duration });
  };

  handleVolumeChange = () => {
    const video = this.videoRef.current;
    this.setState({
      volume: video.volume,
      muted: video.muted,
    });
  };

  handlePlay = () => this.setState({ playing: true });
  handlePause = () => this.setState({ playing: false });
  handleEnded = () => this.setState({ playing: false });

  // Control actions
  togglePlay = () => {
    const video = this.videoRef.current;
    if (video.paused) video.play();
    else video.pause();
  };

  handleSeek = (e) => {
    const progressBar = this.progressRef.current;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percent = clickX / width;
    const video = this.videoRef.current;
    video.currentTime = percent * video.duration;
  };

  handleVolumeSeek = (e) => {
    const volumeBar = this.volumeRef.current;
    if (!volumeBar) return;
    const rect = volumeBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const newVolume = Math.min(1, Math.max(0, clickX / width));
    const video = this.videoRef.current;
    video.volume = newVolume;
    video.muted = false;
  };

  toggleMute = () => {
    const video = this.videoRef.current;
    video.muted = !video.muted;
  };

  toggleFullscreen = () => {
    const container = this.videoRef.current.parentElement;
    if (!document.fullscreenElement) {
      container.requestFullscreen();
      this.setState({ fullscreen: true });
    } else {
      document.exitFullscreen();
      this.setState({ fullscreen: false });
    }
  };

  formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  render() {
    const { playing, currentTime, duration, volume, muted } = this.state;
    const progressPercent = (currentTime / (duration || 1)) * 100;
    const volumePercent = (muted ? 0 : volume) * 100;

    return (
      <div className="video-player-container">
        <video
          ref={this.videoRef}
          className="video-element"
        />

        <div className="custom-controls">
          <div className="progress-bar-container" ref={this.progressRef} onClick={this.handleSeek}>
            <div className="progress-bar-filled" style={{ width: `${progressPercent}%` }} />
          </div>

          <div className="controls-row">
            <div className="left-controls">
              <button className="control-button" onClick={this.togglePlay}>
                {playing ? "⏸️" : "▶️"}
              </button>
              <span className="time-display">
                {this.formatTime(currentTime)} / {this.formatTime(duration)}
              </span>
            </div>

            <div className="right-controls">
              <div className="volume-control">
                <button className="control-button" onClick={this.toggleMute}>
                  {muted || volume === 0 ? "🔇" : volume < 0.5 ? "🔉" : "🔊"}
                </button>
                <div className="volume-slider-container" ref={this.volumeRef} onClick={this.handleVolumeSeek}>
                  <div className="volume-slider-filled" style={{ width: `${volumePercent}%` }} />
                </div>
              </div>
              <button className="control-button" onClick={this.toggleFullscreen}>
                ⛶
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default VideoPlayerHLS;